import { Trans, useTranslation } from "next-i18next"
import React, { useMemo } from "react"
import { Col, Row } from "react-styled-flexboxgrid"
import styled from "styled-components"
import { theme } from "@aistihealth/web-common/src/util/styles/theme"
import { Heading } from "@aistihealth/web-common/src/components/heading/Heading"
import { Tabs } from "@aistihealth/web-common/src/components/tabs/Tabs"
import { Icon } from "@aistihealth/web-common/src/components/icon/Icon"
import { Card } from "@aistihealth/web-common/src/components/card/Card"
import { CardHeader } from "@aistihealth/web-common/src/components/card/components/CardHeader"
import { CardContent } from "@aistihealth/web-common/src/components/card/components/CardContent"
import { Paragraph } from "@aistihealth/web-common/src/components/paragraph/Paragraph"
import { SingleColLayout } from "@aistihealth/web-common/src/components/single-col-layout/SingleColLayout"
import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import { CardI18nProps, defaultIfString } from "../../util/typing/i18n"
import { Section, SectionProps } from "../section/Section"
import { getStartedSectionProps } from "../../util/styles/common-section-props"

const CardCol = styled(Col)`
  display: flex;
  margin-bottom: ${theme.spacings.medium};
`

const StyledCard = styled(Card)`
  height: 100%;
`

const StyledIcon = styled(Icon)`
  ${theme.textStyle("heading", "xxlarge", "medium")};

  ${theme.breakpoints.sm} {
    ${theme.textStyle("heading", "xlarge", "medium")};
  }

  margin-bottom: ${theme.spacings.medium};
`

export interface GetStartedSectionProps {
  sectionProps?: SectionProps
  /**
   * What content to show. Tabbed shows all possible content as tabs.
   */
  variant: "organizations" | "individuals" | "tabbed"
}

type GetStartedSectionContentProps = Exclude<
  GetStartedSectionProps,
  "sectionProps"
>

const GetStartedTabContent: React.FunctionComponent<GetStartedSectionContentProps> = ({
  variant,
}) => {
  const { t } = useTranslation()
  const getStartedCards: CardI18nProps[] = defaultIfString(
    t(`getStartedSection.${variant}.cards`, {
      returnObjects: true,
    }),
    [],
  )
  return (
    <>
      <Row>
        {getStartedCards.map(({ description, title, icon }) => {
          return (
            <CardCol xs={12} md={4} key={description}>
              <StyledCard bottomMargin>
                <CardHeader>
                  <StyledIcon icon={icon} color="orange" />
                  <Heading as="h4" size="large" topMargin={false}>
                    {title}
                  </Heading>
                </CardHeader>
                <CardContent>
                  <Paragraph>{description}</Paragraph>
                </CardContent>
              </StyledCard>
            </CardCol>
          )
        })}
      </Row>
    </>
  )
}

const GetStartedContent: React.FunctionComponent<GetStartedSectionContentProps> = ({
  variant,
}) => {
  const { t } = useTranslation()

  const tabs = useMemo(
    () => [
      {
        value: t("getStartedSection.organizations.tabTitle"),
      },
      {
        value: t("getStartedSection.individuals.tabTitle"),
      },
    ],
    [t],
  )

  const tabPanels = useMemo(
    () => [
      {
        content: <GetStartedTabContent variant="organizations" />,
      },
      {
        content: <GetStartedTabContent variant="individuals" />,
      },
    ],
    [],
  )

  switch (variant) {
    case "organizations":
    case "individuals":
      return <GetStartedTabContent variant={variant} />
    case "tabbed":
      return <Tabs tabs={tabs} tabPanels={tabPanels} />
    default:
      return assertNever(variant)
  }
}

export const GetStartedSection: React.FunctionComponent<GetStartedSectionProps> = ({
  variant,
  sectionProps,
}) => {
  const getStartedProps = sectionProps || getStartedSectionProps[0]
  const { backgroundColor, bottomCut } = getStartedProps

  return (
    <Section backgroundColor={backgroundColor} bottomCut={bottomCut}>
      <SingleColLayout>
        <Heading as="h3" size="xlarge" align="center">
          <Trans i18nKey="getStartedSection.header" />
        </Heading>
        <GetStartedContent variant={variant} />
      </SingleColLayout>
    </Section>
  )
}
